"use client";

import type { ChatStatus, ToolUIPart as ToolPartType } from "ai";
import {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ToolOutput } from "@/components/ai-elements/tool";
import { VizRenderer } from "@/components/ai-elements/viz";
import type { AgentUIMessage } from "@/lib/ai/types";
import {
  areVizOutputToolCallIdsEqual,
  buildVizToolPartRenderModel,
  buildVizToolPartRenderSignature,
  type DatasetIndex,
  type VizToolPartRenderModel,
} from "@/lib/ai/visualization/client/hooks/viz-tool-part-state";
import type { VizRenderOutput } from "@/lib/ai/visualization/types";
import { useElementInView } from "@/lib/ui/hooks/use-element-in-view";
import { useVizStreamTree, type VizStreamStateApi } from "./use-viz-stream";

export type VizToolPartRendererProps = {
  part: ToolPartType;
  message: AgentUIMessage;
  chatStatus: ChatStatus;
  isLastMessage: boolean;
  historyDatasetIndex: DatasetIndex;
  historyVizOutputIndex: Map<string, VizRenderOutput>;
  isDuplicateOutputPart: boolean;
  vizOutputToolCallIds: Set<string>;
  vizStream?: VizStreamStateApi;
};

export const VizToolPartRenderer = memo(
  function VizToolPartRenderer({
    part,
    message,
    chatStatus,
    isLastMessage,
    historyDatasetIndex,
    historyVizOutputIndex,
    isDuplicateOutputPart,
    vizOutputToolCallIds,
    vizStream,
  }: VizToolPartRendererProps): ReactElement | null {
    const toolCallId = part.toolCallId;
    const scopedToolCallId =
      typeof toolCallId === "string" ? toolCallId : undefined;
    const streamTree = useVizStreamTree(vizStream, scopedToolCallId);
    const renderModel = useMemo(
      () =>
        buildVizToolPartRenderModel({
          part,
          message,
          chatStatus,
          isLastMessage,
          historyDatasetIndex,
          historyVizOutputIndex,
          isDuplicateOutputPart,
          vizOutputToolCallIds,
          streamTree,
        }),
      [
        chatStatus,
        historyDatasetIndex,
        historyVizOutputIndex,
        isDuplicateOutputPart,
        isLastMessage,
        message,
        part,
        streamTree,
        vizOutputToolCallIds,
      ],
    );

    if (renderModel.shouldHidePart) {
      return null;
    }

    if (
      !renderModel.shouldShowVisualization ||
      !renderModel.tree ||
      !renderModel.vizData
    ) {
      if (
        renderModel.shouldShowFailureOutput ||
        renderModel.shouldShowRawOutput
      ) {
        return (
          <ToolOutput
            className="rounded-md border border-border/60 bg-card/40"
            density="compact"
            errorText={renderModel.errorText}
            output={
              renderModel.shouldShowFailureOutput
                ? renderModel.failureOutput
                : renderModel.toolOutput
            }
          />
        );
      }
      return null;
    }

    return (
      <div className="w-full space-y-2">
        {renderModel.narrativeTexts.beforeCardText ? (
          <p className="text-sm leading-relaxed">
            {renderModel.narrativeTexts.beforeCardText}
          </p>
        ) : null}
        <LazyVizRenderer
          tree={renderModel.tree}
          data={renderModel.vizData}
          isStreaming={renderModel.isStreaming}
          keepMountedWhileLastMessage={isLastMessage}
        />
        {renderModel.narrativeTexts.postCardAnalysisText ? (
          <div className="mt-2 space-y-2">
            <p className="text-sm leading-relaxed">
              {renderModel.narrativeTexts.postCardAnalysisText}
            </p>
          </div>
        ) : null}
      </div>
    );
  },
  (previous, next) =>
    previous.chatStatus === next.chatStatus &&
    previous.isLastMessage === next.isLastMessage &&
    previous.message.id === next.message.id &&
    previous.message.role === next.message.role &&
    previous.historyDatasetIndex === next.historyDatasetIndex &&
    previous.historyVizOutputIndex === next.historyVizOutputIndex &&
    previous.isDuplicateOutputPart === next.isDuplicateOutputPart &&
    previous.vizStream === next.vizStream &&
    areVizOutputToolCallIdsEqual(
      previous.vizOutputToolCallIds,
      next.vizOutputToolCallIds,
    ) &&
    buildVizToolPartRenderSignature(previous.part) ===
      buildVizToolPartRenderSignature(next.part),
);

type LazyVizRendererProps = {
  tree: VizRenderOutput["tree"];
  data: NonNullable<VizToolPartRenderModel["vizData"]>;
  isStreaming: boolean;
  keepMountedWhileLastMessage: boolean;
};

const VIZ_RENDERER_ROOT_MARGIN = "320px 0px";
const VIZ_RENDERER_EXIT_DEBOUNCE_MS = 180;
const VIZ_RENDERER_PLACEHOLDER_HEIGHT_PX = 260;

/**
 * ビューポート近傍に入った時だけ可視化本体をマウントする。
 *
 * @remarks
 * 会話領域の実際の scroll viewport を root にして、画面内へ戻ったカードを再描画する。
 * 監視対象へ `content-visibility: auto` も指定すると、Chrome では初期表示時に root の
 * 上側へ外れていた要素の再進入通知が欠落するため、表示制御は IntersectionObserver に一本化する。
 * 最新 assistant 応答のカードは、一度描画できたら応答完了直後もそのまま保持する。
 * live streaming の step 境界で `chatStatus` が一瞬 ready へ戻ると、visibility 判定だけに
 * 依存した earlier card が placeholder へ戻って「次のカードが来るまで描けない」状態になるため。
 */
function LazyVizRenderer({
  tree,
  data,
  isStreaming,
  keepMountedWhileLastMessage,
}: LazyVizRendererProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [reservedHeight, setReservedHeight] = useState<number | null>(null);
  const [hasRendered, setHasRendered] = useState(false);
  const hasRenderedRef = useRef(false);
  const resolveObservationRoot = useCallback(() => {
    return container?.closest(".chat-scroll-viewport") ?? null;
  }, [container]);
  const { targetRef, isInView } = useElementInView<HTMLDivElement>({
    rootMargin: VIZ_RENDERER_ROOT_MARGIN,
    threshold: 0,
    freezeOnceVisible: false,
    exitDebounceMs: VIZ_RENDERER_EXIT_DEBOUNCE_MS,
    resolveRoot: resolveObservationRoot,
  });
  // 最新応答に属するカードは、一度描けたら ready へ戻った直後も保持して
  // step 間の placeholder への巻き戻りを避ける。
  const shouldRender =
    isStreaming || isInView || (hasRendered && keepMountedWhileLastMessage);
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      targetRef(node);
      setContainer(node);
    },
    [targetRef],
  );

  useEffect(() => {
    if (hasRenderedRef.current) {
      return;
    }
    if (!isStreaming && !isInView) {
      return;
    }

    hasRenderedRef.current = true;
    setHasRendered(true);
  }, [isInView, isStreaming]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (!shouldRender || !container) {
      return;
    }

    const updateReservedHeight = () => {
      // lazy unmount 後も直前の高さを維持し、chat 全体の高さ変動で scroll が引き戻されるのを防ぐ。
      const nextHeight = Math.ceil(container.getBoundingClientRect().height);
      if (nextHeight <= 0) {
        return;
      }
      setReservedHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight,
      );
    };

    updateReservedHeight();

    if (!("ResizeObserver" in window)) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateReservedHeight();
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [container, shouldRender]);

  return (
    <div ref={setContainerRef} className="w-full">
      {shouldRender ? (
        <VizRenderer tree={tree} data={data} isStreaming={isStreaming} />
      ) : (
        <div
          aria-hidden
          data-viz-placeholder
          className="w-full rounded-md border border-border/40 bg-muted/20"
          style={{
            height: reservedHeight ?? VIZ_RENDERER_PLACEHOLDER_HEIGHT_PX,
          }}
        />
      )}
    </div>
  );
}
